import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { ReportHeader, SummaryCards, FilterPanel, ExportButtons } from '../../components/reports';
import { D3PieChart, D3BarChart } from '../../components/charts';
import { getSecuredPartyAnalysis } from '../../services/project750kService';
import { exportToExcel, exportToPDF } from '../../utils/exportUtils';

const PageContainer = styled.div`
  padding: 20px;
  max-width: 1400px;
  margin: 0 auto;
`;

const ChartsRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin-bottom: 30px;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const ChartSection = styled.div`
  background: white;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const TableContainer = styled.div`
  background: white;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;

  th, td {
    padding: 12px;
    text-align: left;
    border-bottom: 1px solid #e0e0e0;
    font-family: 'Segoe UI', sans-serif;
  }

  th {
    background-color: #0077B5;
    color: white;
    font-weight: 600;
  }

  tbody tr:hover {
    background-color: #f8f9fa;
  }
`;

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 40px;
  font-size: 18px;
  color: #0077B5;
`;

const ErrorMessage = styled.div`
  background-color: #ffebee;
  color: #c62828;
  padding: 16px;
  border-radius: 4px;
  margin-bottom: 20px;
`;

const InfoBanner = styled.div`
  background-color: #e3f2fd;
  color: #1565c0;
  padding: 12px 16px;
  border-radius: 4px;
  margin-bottom: 20px;
  font-family: 'Segoe UI', sans-serif;
  font-size: 14px;
`;

const SecuredPartyAnalysis = () => {
  const [securedParties, setSecuredParties] = useState([]);
  const [chartData, setChartData] = useState([]);
  const [summary, setSummary] = useState(null);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [county, setCounty] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async (countyFilter = null) => {
    setLoading(true);
    setError(null);

    try {
      const result = await getSecuredPartyAnalysis(countyFilter);
      setSecuredParties(result.securedParties || []);
      setChartData(result.chartData || []);
      setSummary(result.summary || {});
      setMeta(result.meta || null);
    } catch (err) {
      setError(`Error loading secured party analysis: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (value) => {
    setCounty(value);
    setTimeout(() => loadData(value || null), 500);
  };

  const handleExportExcel = useCallback(async () => {
    try {
      await exportToExcel(securedParties, 'Secured_Party_Analysis', 'Lenders', { county });
    } catch (err) {
      setError(`Export failed: ${err.message}`);
    }
  }, [securedParties, county]);

  const handleExportPDF = useCallback(async () => {
    try {
      await exportToPDF({
        title: 'Secured Party Market Share Report',
        data: securedParties.slice(0, 100),
        chartElementId: 'pie-chart',
        summary: {
          'Top Lender': summary?.topLender || 'N/A',
          'Top Lender Filings': summary?.topLenderFilings || 0,
          'Total Lenders': summary?.totalLenders || 0
        },
        filename: 'Secured_Party_Analysis',
        filters: { county }
      });
    } catch (err) {
      setError(`PDF export failed: ${err.message}`);
    }
  }, [securedParties, summary, county]);

  const filterConfig = [
    {
      type: 'text',
      label: 'County Filter',
      value: county,
      onChange: handleFilterChange,
      placeholder: 'e.g., YUMA'
    }
  ];

  const summaryCards = summary ? [
    { label: 'Total Lenders', value: summary.totalLenders },
    { label: 'Top Lender', value: summary.topLender },
    { label: 'Top Lender Filings', value: summary.topLenderFilings }
  ] : [];

  // Prepare pie chart data (top 10 with market share)
  const pieChartData = chartData.map(sp => ({
    label: sp.name.length > 25 ? sp.name.substring(0, 25) + '...' : sp.name,
    value: parseInt(sp.marketShare)
  }));

  // Prepare bar chart data
  const barChartData = chartData.map(sp => ({
    label: sp.name.length > 30 ? sp.name.substring(0, 30) + '...' : sp.name,
    value: sp.filings
  }));

  if (loading) return <LoadingSpinner>Loading lender analysis...</LoadingSpinner>;

  return (
    <PageContainer>
      <ReportHeader icon="🏦" title="Lender Analysis" />

      {error && <ErrorMessage>{error}</ErrorMessage>}

      {meta && (
        <InfoBanner>
          ℹ️ Market share based on <strong>{meta.recordsScanned?.toLocaleString() || 0} records</strong>
          {meta.hasMore && ' (sample analysis)'}
        </InfoBanner>
      )}

      <FilterPanel filters={filterConfig} />
      
      <SummaryCards cards={summaryCards} />
      
      <ExportButtons
        onExportExcel={handleExportExcel}
        onExportPDF={handleExportPDF}
        loading={loading}
      />

      <ChartsRow>
        <ChartSection id="pie-chart">
          <h3 style={{ fontFamily: 'Segoe UI', marginBottom: '20px' }}>Market Share (Top 10 Lenders)</h3>
          <D3PieChart
            data={pieChartData}
            labelKey="label"
            valueKey="value"
            height="500px"
          />
        </ChartSection>

        <ChartSection id="bar-chart">
          <h3 style={{ fontFamily: 'Segoe UI', marginBottom: '20px' }}>Filings per Lender (Top 10)</h3>
          <D3BarChart
            data={barChartData}
            xKey="label"
            yKey="value"
            yLabel="Filing Count"
            horizontal={true}
            height="500px"
          />
        </ChartSection>
      </ChartsRow>

      <TableContainer>
        <h3 style={{ fontFamily: 'Segoe UI', marginBottom: '20px' }}>
          All Secured Parties ({securedParties.length} total)
        </h3>
        <Table>
          <thead>
            <tr>
              <th>Rank</th>
              <th>Lender Name</th>
              <th>Total Filings</th>
              <th>Market Share</th>
            </tr>
          </thead>
          <tbody>
            {securedParties.slice(0, 100).map((sp, index) => {
              const totalFilings = securedParties.reduce((sum, p) => sum + p.filings, 0);
              const marketShare = ((sp.filings / totalFilings) * 100).toFixed(2);
              return (
                <tr key={index}>
                  <td>{index + 1}</td>
                  <td><strong>{sp.name}</strong></td>
                  <td>{sp.filings.toLocaleString()}</td>
                  <td>{marketShare}%</td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      </TableContainer>
    </PageContainer>
  );
};

export default SecuredPartyAnalysis;

